import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DashboardDocument,
  DashboardDto,
  FolderDocument,
  FolderDto,
  FolderSummaryDto,
  Scene3DDocument,
  Scene3DDto,
  SectionDto,
  SensorDto,
  VersionConflict,
} from '@atnt/shared';
import { api, ApiError } from '@/shared/api/client';

/**
 * Constructor API hooks (docs/design/02-constructor.md §11; apps/api/src/modules/constructor/routes.ts).
 * `tenantId` accepts the tenant UUID or slug (the API resolves both); keys follow `['tenant', tenantId, …]`.
 */

export type { SensorDto } from '@atnt/shared';

const t = (tenantId: string) => `/tenants/${encodeURIComponent(tenantId)}`;

export const constructorKeys = {
  all: (tenantId: string) => ['tenant', tenantId] as const,
  dashboard: (tenantId: string) => ['tenant', tenantId, 'dashboard'] as const,
  sections: (tenantId: string) => ['tenant', tenantId, 'sections'] as const,
  folders: (tenantId: string, sectionId: string) => ['tenant', tenantId, 'sections', sectionId, 'folders'] as const,
  folder: (tenantId: string, folderId: string) => ['tenant', tenantId, 'folders', folderId] as const,
  scene: (tenantId: string, sectionId: string) => ['tenant', tenantId, 'sections', sectionId, 'scene'] as const,
  sensors: (tenantId: string) => ['tenant', tenantId, 'sensors'] as const,
};

/* ───────────────────────── queries ───────────────────────── */

export const dashboardQuery = (tenantId: string) =>
  queryOptions({ queryKey: constructorKeys.dashboard(tenantId), queryFn: () => api.get<DashboardDto>(`${t(tenantId)}/dashboard`) });

/** Sections in card order. Editors also receive hidden sections (filter `!s.hidden` for viewer UI). */
export const sectionsQuery = (tenantId: string) =>
  queryOptions({ queryKey: constructorKeys.sections(tenantId), queryFn: () => api.get<SectionDto[]>(`${t(tenantId)}/sections`) });

export const foldersQuery = (tenantId: string, sectionId: string) =>
  queryOptions({
    queryKey: constructorKeys.folders(tenantId, sectionId),
    queryFn: () => api.get<FolderSummaryDto[]>(`${t(tenantId)}/sections/${encodeURIComponent(sectionId)}/folders`),
  });

export const folderQuery = (tenantId: string, folderId: string) =>
  queryOptions({ queryKey: constructorKeys.folder(tenantId, folderId), queryFn: () => api.get<FolderDto>(`${t(tenantId)}/folders/${encodeURIComponent(folderId)}`) });

export const sceneQuery = (tenantId: string, sectionId: string) =>
  queryOptions({
    queryKey: constructorKeys.scene(tenantId, sectionId),
    queryFn: () => api.get<Scene3DDto>(`${t(tenantId)}/sections/${encodeURIComponent(sectionId)}/scene`),
  });

export const sensorsQuery = (tenantId: string) =>
  queryOptions({ queryKey: constructorKeys.sensors(tenantId), queryFn: () => api.get<SensorDto[]>(`${t(tenantId)}/sensors`), staleTime: 5 * 60_000 });

interface LiveQueryOptions {
  /** viewers re-poll documents so a save by an editor shows up without a reload (no `pageUpdated` push yet) */
  refetchInterval?: number | false;
  enabled?: boolean;
}

export const useDashboard = (tenantId: string, opts: LiveQueryOptions = {}) => useQuery({ ...dashboardQuery(tenantId), ...opts });
export const useSections = (tenantId: string, opts: LiveQueryOptions = {}) => useQuery({ ...sectionsQuery(tenantId), ...opts });
export const useFolders = (tenantId: string, sectionId: string | null | undefined, opts: LiveQueryOptions = {}) =>
  useQuery({ ...foldersQuery(tenantId, sectionId ?? ''), ...opts, enabled: Boolean(sectionId) && opts.enabled !== false });
export const useFolder = (tenantId: string, folderId: string | null | undefined, opts: LiveQueryOptions = {}) =>
  useQuery({ ...folderQuery(tenantId, folderId ?? ''), ...opts, enabled: Boolean(folderId) && opts.enabled !== false });
export const useScene = (tenantId: string, sectionId: string | null | undefined, opts: LiveQueryOptions = {}) =>
  useQuery({ ...sceneQuery(tenantId, sectionId ?? ''), ...opts, enabled: Boolean(sectionId) && opts.enabled !== false });
export const useSensors = (tenantId: string, opts: LiveQueryOptions = {}) => useQuery({ ...sensorsQuery(tenantId), ...opts });

/* ───────────────────────── saves (optimistic locking) ───────────────────────── */

export interface SaveDocumentInput<D> {
  document: D;
  /** the `version` the draft was loaded from */
  baseVersion: number;
}

/**
 * Details of a 409 `version_conflict` (another user saved first), or null for any other error:
 * `const conflict = getVersionConflict(mutation.error)` → show "Qayta yuklash" / "Ustiga yozish"
 * (overwrite = save again with `baseVersion: conflict.currentVersion`).
 */
export function getVersionConflict(err: unknown): VersionConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409 || err.code !== 'version_conflict') return null;
  const p = err.problem as unknown as Partial<VersionConflict>;
  return {
    code: 'version_conflict',
    currentVersion: typeof p.currentVersion === 'number' ? p.currentVersion : 0,
    updatedAt: typeof p.updatedAt === 'string' ? p.updatedAt : '',
    updatedBy: p.updatedBy ?? null,
  };
}

export const isVersionConflict = (err: unknown) => getVersionConflict(err) !== null;

/** `PUT /tenants/:t/dashboard/document` — the whole dashboard document. */
export function useSaveDashboard(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveDocumentInput<DashboardDocument>) => api.put<DashboardDto>(`${t(tenantId)}/dashboard/document`, { ...input }),
    onSuccess: (dto) => qc.setQueryData(constructorKeys.dashboard(tenantId), dto),
  });
}

/** `PUT /tenants/:t/folders/:id/document` — updates the folder cache, its tab badge (pinCount) and section counts. */
export function useSaveFolder(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, ...input }: SaveDocumentInput<FolderDocument> & { folderId: string }) =>
      api.put<FolderDto>(`${t(tenantId)}/folders/${encodeURIComponent(folderId)}/document`, { ...input }),
    onSuccess: (dto) => {
      qc.setQueryData(constructorKeys.folder(tenantId, dto.id), dto);
      qc.setQueryData<FolderSummaryDto[]>(constructorKeys.folders(tenantId, dto.sectionId), (list) =>
        list?.map((f) => (f.id === dto.id ? { id: dto.id, sectionId: dto.sectionId, name: dto.name, sortOrder: dto.sortOrder, isDefault: dto.isDefault, pinCount: dto.pinCount, version: dto.version, updatedAt: dto.updatedAt } : f)),
      );
      void qc.invalidateQueries({ queryKey: constructorKeys.sections(tenantId) });
    },
  });
}

/** `PUT /tenants/:t/sections/:id/scene` — the 3D scene document. */
export function useSaveScene(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, ...input }: SaveDocumentInput<Scene3DDocument> & { sectionId: string }) =>
      api.put<Scene3DDto>(`${t(tenantId)}/sections/${encodeURIComponent(sectionId)}/scene`, { ...input }),
    onSuccess: (dto) => qc.setQueryData(constructorKeys.scene(tenantId, dto.sectionId), dto),
  });
}
